import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import DevToolbar from "./components/DevToolbar";

import PortalLayout from "./components/portal/PortalLayout";
import StudentSidebar from "./components/portal/StudentSidebar";
import CounselorSidebar from "./components/portal/CounselorSidebar";

import ProtectedRoute from "./components/misc/ProtectedRoute";
import LandingRedirect from "./components/misc/LandingRedirect";

// Loading fallback component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-white dark:bg-[#0B0B0C] flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500 dark:border-violet-300"></div>
  </div>
);

// ============================================================================
// Lazy-loaded Pages (Code Splitting)
// ============================================================================

// Legal/Auth Pages
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));

// Waitlist Pages
const StudentWaitlist = lazy(() => import("./pages/StudentWaitlist"));
const CounselorWaitlist = lazy(() => import("./pages/CounselorWaitlist"));
const CollegesWaitlist = lazy(() => import("./pages/CollegesWaitlist"));
const StudentIntakeForm = lazy(() => import("./pages/StudentIntakeForm"));

// Student Pages
const StudentDashboard = lazy(() => import("./pages/student/StudentDashboard"));
const StudentColleges = lazy(() => import("./pages/student/StudentColleges"));
const StudentEssays = lazy(() => import("./pages/student/StudentEssays"));
const StudentEssaysList = lazy(
  () => import("./pages/student/StudentEssaysList"),
);
const StudentSupplements = lazy(
  () => import("./pages/student/StudentSupplements"),
);
const StudentActivities = lazy(
  () => import("./pages/student/StudentActivities"),
);
const StudentFinancialAid = lazy(
  () => import("./pages/student/StudentFinancialAid"),
);
const StudentRateMyChance = lazy(
  () => import("./pages/student/StudentRateMyChance"),
);
const StudentResearch = lazy(() => import("./pages/student/StudentResearch"));
const StudentCounselor = lazy(() => import("./pages/student/StudentCounselor"));
const StudentCalendar = lazy(() => import("./pages/student/StudentCalendar"));
const StudentProfile = lazy(() => import("./pages/student/StudentProfile"));
const StudentChat = lazy(() => import("./pages/student/StudentChat"));

// Counselor Pages
const CounselorDashboard = lazy(
  () => import("./pages/counselor/CounselorDashboard"),
);
const CounselorColleges = lazy(
  () => import("./pages/counselor/CounselorColleges"),
);
const CounselorEssays = lazy(() => import("./pages/counselor/CounselorEssays"));
const CounselorStudentEssays = lazy(
  () => import("./pages/counselor/CounselorStudentEssays"),
);
const CounselorEssayViewer = lazy(
  () => import("./pages/counselor/CounselorEssayViewer"),
);
const CounselorSupplements = lazy(
  () => import("./pages/counselor/CounselorSupplements"),
);
const CounselorStudentSupplements = lazy(
  () => import("./pages/counselor/CounselorStudentSupplements"),
);
const CounselorCollegeSupplements = lazy(
  () => import("./pages/counselor/CounselorCollegeSupplements"),
);
const CounselorSupplementViewer = lazy(
  () => import("./pages/counselor/CounselorSupplementViewer"),
);
const CounselorActivities = lazy(
  () => import("./pages/counselor/CounselorActivities"),
);
const CounselorStudentActivities = lazy(
  () => import("./pages/counselor/CounselorStudentActivities"),
);
const CounselorFinancialAid = lazy(
  () => import("./pages/counselor/CounselorFinancialAid"),
);
const CounselorResearch = lazy(
  () => import("./pages/counselor/CounselorResearch"),
);
const CounselorChats = lazy(() => import("./pages/counselor/CounselorChats"));
const CounselorCalendar = lazy(
  () => import("./pages/counselor/CounselorCalendar"),
);
const CounselorProfile = lazy(
  () => import("./pages/counselor/CounselorProfile"),
);
const CounselorInviteStudent = lazy(
  () => import("./pages/counselor/CounselorInviteStudent"),
);
const CounselorStudentLists = lazy(
  () => import("./pages/counselor/CounselorStudentLists"),
);
const CounselorStudentDetail = lazy(
  () => import("./pages/counselor/CounselorStudentDetail"),
);

// Marketplace Pages
const DevAccess = lazy(() => import("./pages/DevAccess"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MarketplaceDashboard = lazy(() => import("./pages/counselor/MarketplaceDashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const CounselorDetailPage = lazy(() => import("./pages/CounselorDetailPage"));

// ============================================================================
// Test Pages (Development Only)
// ============================================================================
// These are only loaded in development mode to prevent bundle bloat in production
const isDev = import.meta.env.DEV;

// Lazy load test pages only in development
const TestSupabase = isDev ? lazy(() => import("./pages/TestSupabase")) : null;
const TestCollegeApi = isDev
  ? lazy(() => import("./pages/TestCollegeApi"))
  : null;
const TestSimple = isDev ? lazy(() => import("./pages/TestSimple")) : null;
const TestEssayAPI = isDev ? lazy(() => import("./pages/TestEssayAPI")) : null;
const TestSupplementalData = isDev
  ? lazy(() => import("./pages/TestSupplementalData"))
  : null;
const TestQuestionnaireDebug = isDev
  ? lazy(() => import("./pages/TestQuestionnaireDebug"))
  : null;

function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingRedirect />} />

        {/* Test Pages - Development Only */}
        {isDev && TestSupabase && (
          <Route path="/dev/test-supabase" element={<TestSupabase />} />
        )}
        {isDev && TestCollegeApi && (
          <Route path="/dev/test-college-api" element={<TestCollegeApi />} />
        )}
        {isDev && TestSimple && (
          <Route path="/dev/test-simple" element={<TestSimple />} />
        )}
        {isDev && TestEssayAPI && (
          <Route path="/dev/test-essay-api" element={<TestEssayAPI />} />
        )}
        {isDev && TestSupplementalData && (
          <Route
            path="/dev/test-supplemental-data"
            element={<TestSupplementalData />}
          />
        )}
        {isDev && TestQuestionnaireDebug && (
          <Route
            path="/dev/test-questionnaire-debug"
            element={<TestQuestionnaireDebug />}
          />
        )}

        {/* Marketplace Dev Bypass */}
        <Route path="/dev" element={<DevAccess />} />

        {/* Marketplace */}
        <Route
          path="/marketplace"
          element={
            <ProtectedRoute>
              <Marketplace />
            </ProtectedRoute>
          }
        />
        {/* /counselor/:id moved below the /counselor workspace block so the
            specific workspace route is declared before the catch-all param
            route. See "Counselor Portal Routes". */}
        <Route path="/onboarding" element={<Onboarding />} />
        <Route
          path="/counselor/dashboard"
          element={
            <ProtectedRoute>
              <MarketplaceDashboard />
            </ProtectedRoute>
          }
        />

        {/* Waitlist Pages */}
        <Route path="/students-waitlist" element={<StudentWaitlist />} />
        <Route path="/counselors-waitlist" element={<CounselorWaitlist />} />
        <Route path="/colleges-waitlist" element={<CollegesWaitlist />} />
        <Route path="/student-intake" element={<StudentIntakeForm />} />

        {/* Legal Pages */}
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Auth Callback */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Student Portal Routes */}
        <Route
          path="/student"
          element={
            <ProtectedRoute>
              <PortalLayout sidebar={<StudentSidebar />} />
            </ProtectedRoute>
          }
        >
          <Route index element={<StudentDashboard />} />
          <Route path="colleges" element={<StudentColleges />} />
          <Route path="essays" element={<StudentEssaysList />} />
          <Route path="supplements" element={<StudentSupplements />} />
          <Route path="supplements/edit/:collegeId/:promptId" element={<StudentSupplements />} />
          <Route path="activities" element={<StudentActivities />} />
          <Route path="financial-aid" element={<StudentFinancialAid />} />
          <Route path="rate-my-chance" element={<StudentRateMyChance />} />
          <Route path="research" element={<StudentResearch />} />
          <Route path="counselor" element={<StudentCounselor />} />
          <Route path="chat" element={<StudentChat />} />
          <Route path="calendar" element={<StudentCalendar />} />
          <Route path="profile" element={<StudentProfile />} />
          {/* Catch-all for unknown student routes — stays in the workspace.
              Was '/marketplace', which ejected students out of the portal. */}
          <Route path="*" element={<Navigate to="/student" replace />} />
        </Route>

        {/* Individual Essay Editor - Outside PortalLayout for full screen */}
        <Route path="/student/essays/:essayId" element={<StudentEssays />} />

        {/* Counselor Portal Routes */}
        <Route
          path="/counselor"
          element={
            <ProtectedRoute>
              <PortalLayout sidebar={<CounselorSidebar />} />
            </ProtectedRoute>
          }
        >
          <Route index element={<CounselorDashboard />} />
          <Route path="colleges" element={<CounselorColleges />} />
          <Route path="student-lists" element={<CounselorStudentLists />} />
          <Route path="students/:studentId" element={<CounselorStudentDetail />} />
          <Route path="essays" element={<CounselorEssays />} />
          <Route path="supplements" element={<CounselorSupplements />} />
          <Route path="activities" element={<CounselorActivities />} />
          <Route
            path="activities/:studentId"
            element={<CounselorStudentActivities />}
          />
          <Route path="financial-aid" element={<CounselorFinancialAid />} />
          <Route path="research" element={<CounselorResearch />} />
          <Route path="chats" element={<CounselorChats />} />
          <Route path="calendar" element={<CounselorCalendar />} />
          <Route path="invite" element={<CounselorInviteStudent />} />
          <Route path="profile" element={<CounselorProfile />} />
          {/* Catch-all for unknown counselor routes */}
          <Route path="*" element={<Navigate to="/counselor" replace />} />
        </Route>

        {/* Marketplace counselor detail — declared AFTER /counselor so the
            workspace route is never shadowed by this param route. Marketplace
            is disconnected from navigation; this stays registered only so the
            code remains intact. */}
        <Route
          path="/counselor/:id"
          element={
            <ProtectedRoute>
              <CounselorDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Counselor Essay Views - Outside PortalLayout for full screen */}
        <Route
          path="/counselor/essays/:studentId"
          element={<CounselorStudentEssays />}
        />
        <Route
          path="/counselor/essays/:studentId/:essayId"
          element={<CounselorEssayViewer />}
        />

        {/* Counselor Supplement Views - Outside PortalLayout for full screen */}
        <Route
          path="/counselor/supplements/:studentId"
          element={<CounselorStudentSupplements />}
        />
        <Route
          path="/counselor/supplements/:studentId/college/:collegeId"
          element={<CounselorCollegeSupplements />}
        />
        <Route
          path="/counselor/supplements/:studentId/:essayId"
          element={<CounselorSupplementViewer />}
        />

        {/* Catch-all for unknown routes - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <DevToolbar />
    </Suspense>
  );
}

export default App;
